import { Divider, TextInput } from "@mantine/core";
import { useAtom } from "jotai";
import { colors, pxToRem } from "theme";

import Button from "components/Button";
import Flex from "components/Flex";
import Image from "components/Image";
import PageLayout from "components/PageLayout";
import Text from "components/Text";

import { useGetBodyShopsByPostCode } from "app/hooks/useGetBodyShopsByPostCode";
import { bodyShopAtom, bodyShopPostalCodeAtom } from "atoms/selectBodyShop";
import { useClientTranslation } from "hooks/useClientTranslation";
import { useDigitalJourneyPageRouting } from "hooks/useDigitalJourneyPageRouting";
import { useEventTracking } from "hooks/useEventTracking";

export const DJSelectBodyShopPage = () => {
  const { t } = useClientTranslation();
  const { trackEvent } = useEventTracking();
  const { onNext, continueCTA } = useDigitalJourneyPageRouting();
  const [selectedBodyShop, setSelectedBodyShop] = useAtom(bodyShopAtom);
  const [bodyShopPostalCode, setBodyShopPostalCode] = useAtom(bodyShopPostalCodeAtom);

  const { data: bodyShops, isLoading } = useGetBodyShopsByPostCode(bodyShopPostalCode);
  const isDisabled = !selectedBodyShop || !bodyShopPostalCode;

  const onContinue = () => {
    trackEvent({ name: "Digital Journey Select Body Shop - continue" });
    onNext();
  };

  return (
    <PageLayout contentSpacing="space-between">
      <Flex px={24} py={24} gap={16} flex={1} mb={16}>
        <Text as="h1" sizeVariant="xl" weight={600}>
          Seleziona la carrozzeria convenzionata più vicina a te
        </Text>
        <TextInput
          name="bodyShopPostalCode"
          value={bodyShopPostalCode}
          onChange={(e) => setBodyShopPostalCode(e.target.value)}
          label={
            <Text weight={700} sizeVariant="md">
              Inserisci il tuo CAP
            </Text>
          }
          maxLength={10}
        />
        {(!bodyShops || bodyShops.length === 0) && bodyShopPostalCode && bodyShopPostalCode.length > 2 && !isLoading && (
          <Flex
            mt={24}
            p={16}
            gap={8}
            direction="row"
            align="flex-start"
            style={{
              background: "#EDF5FF",
              border: "1px",
              borderColor: colors.blue[10],
              borderRadius: pxToRem[8],
            }}
          >
            <Image
              style={{ marginTop: pxToRem[2] }}
              width={14}
              src="/assets/svg/info-icon.svg"
              alt="Accident Circumstance Info"
            />
            <Text>
              Spiacenti, a questo CAP non ci sono carrozzerie convenzionate. Indica il CAP di un'altra zona vicina a te.
            </Text>
          </Flex>
        )}
        {bodyShops && bodyShops.length > 0 && (
          <Flex>
            <Text>Seleziona la carrozzeria convenzionata</Text>
            <Divider mt={8} mb={16} size="xs" style={{ borderColor: "#D9D9D9" }} />
            <Flex gap={16}>
              {bodyShops.map((bodyShop) => (
                <Button
                  aria-labelledby={`driverPresent-${bodyShop.id}-label`}
                  aria-checked={bodyShop.id === selectedBodyShop?.id}
                  role="radio"
                  key={`driverPresent-${bodyShop.id}-label`}
                  onClick={() => setSelectedBodyShop(bodyShop)}
                  typeVariant={bodyShop.id === selectedBodyShop?.id ? "primary" : "secondary"}
                  sizeVariant="small"
                  sx={{
                    "& div": {
                      justifyContent: "flex-start",
                    },
                  }}
                >
                  <Flex align="start">
                    <label
                      id={`driverPresent-${bodyShop.id}-label`}
                      style={{
                        fontWeight: 700,
                        color: bodyShop.id === selectedBodyShop?.id ? colors.white : colors.grey[90],
                      }}
                    >
                      {bodyShop.name}
                    </label>
                    <Text
                      weight={400}
                      sx={{
                        "&": {
                          color: bodyShop.id === selectedBodyShop?.id ? colors.white : colors.grey[90],
                        },
                      }}
                    >
                      {bodyShop.address}, {bodyShop.postalCode} {bodyShop.city}
                    </Text>
                  </Flex>
                </Button>
              ))}
            </Flex>
          </Flex>
        )}
      </Flex>
      <Flex px={24} py={24} gap={16}>
        <Button disabled={isDisabled} onClick={onContinue}>
          {continueCTA}
        </Button>
      </Flex>
    </PageLayout>
  );
};
